import {
  ItemResult,
  ItemResultType,
  makeItemResult,
} from 'js/components/controls/search-item-result';
import { CmsFilters } from 'js/model/cms/cms-browse-page';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import {
  NamedEntityOutput,
  namedEntityOutputName,
} from 'js/model/rainbow/NamedEntityOutput';
import { NamedEntityCountOutput } from 'js/model/rainbow/NamedEntityCountOutput';
import { substitute } from 'js/helpers/i18n';
import { AggregationEntity } from 'js/model/view/browse-page/browse-aggregations';

const section = 'filter';

export function defaultItemName(
  cmsFilters: CmsFilters,
  aggregations: Aggregations,
  selectedTreatments: NamedEntityOutput[],
  selectedTreatmentType?: NamedEntityOutput,
  selectedVenueType?: NamedEntityOutput
): string {
  const items = buildItemResults(
    cmsFilters,
    aggregations,
    selectedTreatments,
    selectedTreatmentType,
    selectedVenueType
  );
  const key = defaultResultItemKey(
    aggregations,
    selectedTreatments,
    selectedTreatmentType,
    selectedVenueType
  );
  const defaultItem = items.find((item) => item.key === key);
  return defaultItem ? defaultItem.label : '';
}

export function buildItemResults(
  cmsFilters: CmsFilters,
  aggregations: Aggregations,
  selectedTreatments: NamedEntityOutput[],
  selectedTreatmentType?: NamedEntityOutput,
  selectedVenueType?: NamedEntityOutput
): ItemResult[] {
  ensureSelectedTreatmentInAggregations(
    aggregations.treatmentCategories,
    selectedTreatments
  );
  ensureSelectedTreatmentTypeInAggregations(
    aggregations.treatmentCategoryGroups,
    selectedTreatmentType
  );
  if (aggregations.venueTypes) {
    ensureSelectedVenueTypeInAggregations(
      aggregations.venueTypes,
      selectedVenueType
    );
  }

  if (
    selectedTreatments.length === 0 &&
    !selectedTreatmentType &&
    !selectedVenueType
  ) {
    const allResult = makeAllTreatmentTypesResult(cmsFilters);
    const treatmentTypesResults = makeResults(
      aggregations.treatmentCategoryGroups,
      ItemResultType.TreatmentType
    );
    return [allResult, ...treatmentTypesResults];
  }
  if (
    selectedVenueType &&
    selectedTreatments.length === 0 &&
    !selectedTreatmentType
  ) {
    let venueTypesResults: ItemResult[] = [];
    if (aggregations.venueTypes) {
      venueTypesResults = makeResults(
        aggregations.venueTypes,
        ItemResultType.VenueType
      );
    }

    return [...venueTypesResults];
  }
  const treatmentTypesResults = selectedTreatmentTypeResults(
    cmsFilters,
    aggregations.treatmentCategoryGroups,
    selectedTreatmentType
  );
  const multiTreatmentResults = selectedTreatmentsResults(selectedTreatments);
  const filterResults = makeResults(
    aggregations.treatmentCategories,
    ItemResultType.Treatments
  );

  return [...treatmentTypesResults, ...multiTreatmentResults, ...filterResults];
}

// If the selected treatment has no results it will not be in the collection
// of treatments in the aggregations. This means that it won't be selectable
// as the default list item. This further means that its name won't be rendered
// in the search input's closed state.
//
// So add it as the the first item of the aggregations treatments.
function ensureSelectedTreatmentInAggregations(
  treatmentCategories: NamedEntityCountOutput[],
  selectedTreatments: NamedEntityOutput[]
): void {
  if (selectedTreatments.length !== 1) {
    return;
  }

  const haveTreatment = treatmentCategories.some(
    (treatment: NamedEntityOutput): boolean =>
      treatment.id === selectedTreatments[0].id
  );
  if (haveTreatment) {
    return;
  }

  const treatmentCategory: NamedEntityCountOutput = {
    ...selectedTreatments[0],
    normalisedName: '',
    count: 0,
  };
  treatmentCategories.unshift(treatmentCategory);
}

// If the selected treatment type has no results it will not be in the collection
// of treatment types in the aggregations. This means that it won't be selectable
// as the default list item. This further means that its name might not be rendered
// in the search input's closed state.
//
// So add it as the the first item of the aggregations treatment types.
function ensureSelectedTreatmentTypeInAggregations(
  treatmentCategoryGroups: NamedEntityCountOutput[],
  selectedTreatmentType?: NamedEntityOutput
): void {
  if (!selectedTreatmentType) {
    return;
  }

  const haveTreatmentType = treatmentCategoryGroups.some(
    (treatmentType: NamedEntityOutput): boolean =>
      treatmentType.id === selectedTreatmentType.id
  );

  if (haveTreatmentType) {
    return;
  }

  const treatmentCategoryGroup: NamedEntityCountOutput = {
    ...selectedTreatmentType,
    normalisedName: '',
    count: 0,
  };
  treatmentCategoryGroups.unshift(treatmentCategoryGroup);
}

function ensureSelectedVenueTypeInAggregations(
  venueTypes: NamedEntityCountOutput[],
  selectedVenueType?: NamedEntityOutput
): void {
  if (!selectedVenueType) {
    return;
  }

  const haveVenueType = venueTypes.some(
    (venueType: NamedEntityOutput): boolean =>
      venueType.id === selectedVenueType.id
  );
  if (haveVenueType) {
    return;
  }

  const venueType: NamedEntityCountOutput = {
    ...selectedVenueType,
    normalisedName: '',
    count: 0,
  };

  venueTypes.unshift(venueType);
}

function makeResults(
  items: AggregationEntity[],
  resultType: ItemResultType
): ItemResult[] {
  return items.map((item): ItemResult => {
    const result = makeItemResult({
      entity: {
        entityType: resultType,
        entityValue: `${item.id}`,
        name: item.name,
      },
      section,
    });

    result.url = item.uri;

    return result;
  });
}

function makeAllTreatmentTypesResult(cmsFilters: CmsFilters): ItemResult {
  return makeItemResult({
    entity: {
      entityType: ItemResultType.TreatmentType,
      entityValue: '',
      name: cmsFilters['treatment-list-all-treatments-label'],
    },
    section,
  });
}

function selectedTreatmentTypeResults(
  cmsFilters: CmsFilters,
  treatmentCategoryGroups: NamedEntityCountOutput[],
  selectedTreatmentType?: NamedEntityOutput
): ItemResult[] {
  if (!selectedTreatmentType) {
    return [];
  }

  const treatmentCategoryGroup = treatmentCategoryGroups.find(
    (group) => group.id === selectedTreatmentType.id
  );
  const count = treatmentCategoryGroup
    ? String(treatmentCategoryGroup.count)
    : undefined;
  // Plural names should always be reasonable for this use.
  const name = namedEntityOutputName(selectedTreatmentType, 2);

  const result = makeItemResult({
    entity: {
      entityType: ItemResultType.TreatmentType,
      entityValue: `${selectedTreatmentType.id}`,
      name: substitute(cmsFilters['treatment-list-treatment-type-all'], name),
    },
    section,
    displayRight: count !== '0' ? count : '',
  });

  return [result];
}

function selectedTreatmentsResults(
  selectedTreatments: NamedEntityOutput[]
): ItemResult[] {
  if (selectedTreatments.length < 2) {
    return [];
  }

  const entityValue = treatmentsResultEntityValue(selectedTreatments);
  const name = `${selectedTreatments[0].name}, ...`;

  return [
    makeItemResult({
      entity: {
        entityType: ItemResultType.Treatments,
        entityValue,
        name,
      },
      section,
    }),
  ];
}

function treatmentsResultEntityValue(
  selectedTreatments: NamedEntityOutput[]
): string {
  return selectedTreatments.map((treatment) => treatment.id).join(',');
}

export function defaultResultItemKey(
  aggregations: Aggregations,
  selectedTreatments: NamedEntityOutput[],
  selectedTreatmentType?: NamedEntityOutput,
  selectedVenueType?: NamedEntityOutput
): string | undefined {
  if (
    selectedTreatments.length === 0 &&
    !selectedTreatmentType &&
    !selectedVenueType
  ) {
    return `filter:${ItemResultType.TreatmentType}:`;
  }

  if (selectedTreatments.length === 0 && selectedTreatmentType) {
    return `filter:${ItemResultType.TreatmentType}:${selectedTreatmentType.id}`;
  }

  if (selectedTreatments.length === 1) {
    return `filter:${ItemResultType.Treatments}:${selectedTreatments[0].id}`;
  }

  if (selectedTreatments.length > 1) {
    const entityValue = treatmentsResultEntityValue(selectedTreatments);
    return `filter:${ItemResultType.Treatments}:${entityValue}`;
  }

  if (selectedTreatmentType) {
    return `filter:${ItemResultType.TreatmentType}:${selectedTreatmentType.id}`;
  }

  if (selectedVenueType) {
    return `filter:${ItemResultType.VenueType}:${selectedVenueType.id}`;
  }
}
